import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/lib/api-error';
import type { AsistentePreview } from '@/types/settings';
import { usePromptFinal } from './use-settings';

/**
 * «Ver el prompt final» (22-sep-2026): muestra el prompt que el sistema le manda al modelo, armado por
 * el BACKEND con los datos reales del negocio (sus instrucciones propias + reglas + catálogo + medios
 * de pago + el protocolo del pedido).
 *
 * Es la pieza que hace que el negocio pueda tener su propio prompt sin ver ni tocar una variable: aquí
 * ve el resultado, y lo que no entiende no puede romperlo (lo arma el backend).
 *
 * Enseña lo ÚLTIMO GUARDADO: la vista previa se arma en el servidor, así que si hay cambios sin guardar
 * en el formulario el modal lo dice y pide guardar.
 */
export interface PromptFinalDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  asistente: AsistentePreview;
  /** El formulario tiene cambios sin guardar (el prompt mostrado es el de la última vez que se guardó). */
  hayCambiosSinGuardar?: boolean;
}

export function PromptFinalDialog({
  open,
  onClose,
  tenantId,
  asistente,
  hayCambiosSinGuardar = false,
}: PromptFinalDialogProps) {
  const { data, isPending, isError, error } = usePromptFinal(tenantId, asistente, open);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        asistente === 'POSTVENTA'
          ? 'Prompt final del asistente de postventa'
          : 'Prompt final del asistente de ventas'
      }
      description="Exactamente lo que el sistema le manda al modelo con tus datos. Las variables (catálogo, medios de pago, pedidos, fecha, teléfono) y el formato del pedido los pone el sistema."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {isPending ? <PageLoader label="Armando el prompt…" /> : null}

      {isError ? <Alert tone="danger">{getErrorMessage(error)}</Alert> : null}

      {data ? (
        <div className="space-y-4">
          {hayCambiosSinGuardar ? (
            <Alert tone="warning">
              Tienes cambios sin guardar: este es el prompt que el bot está usando <strong>ahora</strong>{' '}
              (lo último guardado). Guarda los cambios y vuelve a abrirlo para verlos aquí.
            </Alert>
          ) : null}

          <p className="text-caption text-ink-muted">
            <strong className="font-semibold text-ink-soft">
              {data.caracteres.toLocaleString('es-CO')}
            </strong>{' '}
            caracteres · {data.productos} producto{data.productos === 1 ? '' : 's'} activo
            {data.productos === 1 ? '' : 's'}
            {data.catalogoCompleto
              ? ''
              : ' (catálogo grande: va el índice completo + el detalle de lo relevante)'}
            {data.tieneInstruccionesPropias
              ? ' · con tus instrucciones propias'
              : ' · sin instrucciones propias (prompt de fábrica)'}
          </p>

          <pre className="scroll-thin max-h-[55vh] overflow-auto whitespace-pre-wrap break-words rounded-card border border-line bg-surface-muted p-4 text-caption leading-relaxed text-ink-soft">
            {data.prompt}
          </pre>
        </div>
      ) : null}
    </Modal>
  );
}
